import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ArrowDown } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import ServiceCard from "@/components/ServiceCard";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CONTACT, SERVICES, CHAPTERS, IMAGES, STORE_URL } from "@/data/site";

const HERO_LINES = ["BOWEGANS CAR CARE.", "YOUR CAR OUR CARE."];

const STATS = [
  { value: "1230+", label: "Verified Fittings" },
  { value: "09", label: "Core Services" },
  { value: "4.9", label: "Customer Rating" },
  { value: "6d", label: "Open Mon – Sat" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y, opacity }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Dark performance car at Bowegans Car Care garage"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E] via-[#0B0C0E]/80 to-[#0B0C0E]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-transparent to-[#0B0C0E]/60" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#3B82F6] sm:text-sm"
          data-testid="hero-eyebrow"
        >
          Palani · Tamil Nadu
        </motion.p>

        <h1 className="font-display mt-6 text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 0 ? "text-[#3B82F6]" : ""}`}
                data-testid={i === 0 ? "hero-brand-line" : undefined}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-gray-300 sm:text-base"
          data-testid="hero-subtitle"
        >
          Palani's performance garage for alloy wheels, leather interiors, high-end audio,
          lighting, body kits, sunfilm and self-healing PPF — fitted with zero compromise.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-booking-button"
            className="font-mono2 group inline-flex items-center gap-3 bg-[#3B82F6] px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB]"
          >
            Book a Slot
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a
            href={CONTACT.phoneLink}
            data-testid="hero-phone-button"
            className="font-mono2 inline-flex items-center gap-3 border border-white/20 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:border-[#3B82F6]"
          >
            <Phone className="h-4 w-4 text-[#3B82F6]" />
            {CONTACT.phone}
          </a>
          <a
            href={CONTACT.phone2Link}
            data-testid="hero-phone2-button"
            className="font-mono2 inline-flex items-center gap-3 border border-white/20 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:border-[#3B82F6]"
          >
            <Phone className="h-4 w-4 text-[#3B82F6]" />
            {CONTACT.phone2}
          </a>
          <a
            href={STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-store-button"
            className="font-mono2 group inline-flex items-center gap-3 border border-[#3B82F6] px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6] transition-colors hover:bg-[#3B82F6] hover:text-white"
          >
            Online Store
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0B0C0E]/90 px-5 py-4 backdrop-blur">
              <p className="font-display text-2xl font-black text-white sm:text-3xl">{s.value}</p>
              <p className="font-mono2 mt-1 text-[10px] uppercase tracking-[0.25em] text-gray-500">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-gray-500" />
      </motion.div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Bowegans Car Care — Aftermarket Car Accessories & Fitting, Palani"
        siteName="Bowegans Car Care"
        image="/logo.png"
        description="Precision aftermarket fitting on NH.83, Palani: alloy wheels, seat covers, car audio, LED lighting, body kits, sunfilm, PPF and premium water wash. Call 7010080038."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AutoPartsStore",
          name: "Bowegans Car Care",
          telephone: ["+917010080038", "+919042438630"],
          address: {
            "@type": "PostalAddress",
            streetAddress: "NH.83, Bypass Road, Shanmuganathi",
            addressLocality: "Palani",
            postalCode: "624601",
            addressCountry: "IN",
          },
        }}
      />
      <Hero />
      <Marquee />

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="home-services-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            testid="home-services-heading"
            eyebrow="What we fit"
            title="Nine crafts. One standard."
            description="Every service is executed by master technicians with genuine parts, warranty bills, and factory-safe fitting methods."
          />
          <Reveal delay={0.2}>
            <Link
              to="/services"
              data-testid="home-services-view-all"
              className="font-mono2 group inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-xs font-bold uppercase tracking-[0.25em] text-gray-300 transition-colors hover:border-[#3B82F6] hover:text-white"
            >
              All Services
              <ArrowRight className="h-4 w-4 text-[#3B82F6] transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-grid">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} withImage />
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#0E1013]" data-testid="home-manifesto-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <SectionHeading
              testid="home-manifesto-heading"
              eyebrow="The manifesto"
              title="Factory is where we start. Not where we stop."
            />
            <Reveal delay={0.2} className="mt-8">
              <div className="relative overflow-hidden border border-[#262A35]">
                <img
                  src={IMAGES.garage}
                  alt="Bowegans Car Care workshop bay"
                  loading="lazy"
                  className="h-72 w-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0E1013] to-transparent" />
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center gap-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.number} delay={i * 0.1}>
                <div className="flex gap-6 border-b border-white/10 pb-10 last:border-0 last:pb-0">
                  <span className="font-display text-stroke text-5xl font-black leading-none">{c.number}</span>
                  <div>
                    <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-400">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="font-mono2 group inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6] transition-colors hover:text-white"
              >
                Read our story
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="home-cta-section">
        <Reveal>
          <div className="relative overflow-hidden border border-[#262A35] bg-[#121418] px-6 py-16 text-center sm:px-12">
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{ background: "radial-gradient(600px circle at 50% 0%, rgba(59,130,246,0.18), transparent 60%)" }}
            />
            <p className="font-mono2 relative text-xs uppercase tracking-[0.3em] text-[#3B82F6]">
              Open Mon – Sat · 8:30 AM – 7:30 PM
            </p>
            <h2 className="font-display relative mx-auto mt-4 max-w-2xl text-2xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
              Your car deserves the Bowegans finish.
            </h2>
            <div className="relative mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="home-cta-book-button"
                className="font-mono2 bg-[#3B82F6] px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:bg-[#2563EB]"
              >
                Book a Slot
              </Link>
              <a
                href={CONTACT.phoneLink}
                data-testid="home-cta-call-button"
                className="font-mono2 inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:border-[#3B82F6]"
              >
                <Phone className="h-4 w-4 text-[#3B82F6]" />
                {CONTACT.phoneDisplay}
              </a>
              <a
                href={CONTACT.phone2Link}
                data-testid="home-cta-call2-button"
                className="font-mono2 inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors hover:border-[#3B82F6]"
              >
                <Phone className="h-4 w-4 text-[#3B82F6]" />
                {CONTACT.phone2Display}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
